import React, { createContext, useCallback, useContext, useRef, useState } from 'react';

import { Modal, ModalBody, ModalHeader } from '@change/ds-core-ui';

import { useI18n } from '@change-corgi/core-react/i18n';

import type {
	ModalContent,
	ModalContentFactory,
	OpenModalResult,
	OpenShareEducationModalFn,
	ShareEducationModalResult,
} from './types';

export type { OpenShareEducationModalFn, OpenModalResult } from './types';

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const ShareEducationModalContext = createContext<OpenShareEducationModalFn | null>(null);

export const ShareEducationModalProvider = ShareEducationModalContext.Provider;

export function useShareEducationModalContext(): OpenShareEducationModalFn | null {
	return useContext(ShareEducationModalContext);
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Provides a single controlled `<Modal>` whose content is swapped at
 * open-time via a factory function.  Meant to be called once — typically in
 * `ShareSlideUp` — and its `openModal` function distributed to channel
 * components via `ShareEducationModalProvider`.
 */
export function useShareEducationModal(): [openModal: OpenShareEducationModalFn, modal: React.ReactElement] {
	const { translate } = useI18n();
	const [isOpen, setIsOpen] = useState(false);
	const [content, setContent] = useState<ModalContent | null>(null);
	const resolverRef = useRef<((result: ShareEducationModalResult | undefined) => void) | null>(null);

	const handleClose = useCallback((result?: ShareEducationModalResult) => {
		setIsOpen(false);

		resolverRef.current?.(result);
		resolverRef.current = null;
	}, []);

	const openModal = useCallback(
		(getContent: ModalContentFactory): OpenModalResult => {
			const newContent = getContent((result) => handleClose(result));

			setContent(newContent);
			setIsOpen(true);

			/**
			 * Need to resolve any prior pending promise so it doesn't hang
			 * in case users open the modal multiple times.
			 */
			resolverRef.current?.(undefined);

			// oxlint-disable-next-line promise/avoid-new
			const promise = new Promise<ShareEducationModalResult | undefined>((resolve) => {
				resolverRef.current = resolve;
			});

			return {
				waitForResult: async () => promise,
			};
		},
		[handleClose],
	);

	const modal = (
		<Modal
			className="[&>div]:min-h-53"
			isOpen={isOpen}
			onClose={() => handleClose(undefined)}
			onOpenChange={(open) => {
				if (!open) handleClose(undefined);
			}}
			transformToSheet
			closeButtonAriaLabel={translate('corgi.components.modal.close_button')}
		>
			<ModalHeader leadingSlot={content?.headingLeadingSlot ?? undefined} title={content?.heading} />
			<ModalBody className="border-stroke-weaker mt-4 border-t pt-0">{content?.body}</ModalBody>
		</Modal>
	);

	return [openModal, modal];
}
